import React, { useMemo, useState } from "react";
import { FiCalendar, FiDownload, FiFilter } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import StatCard from "../components/ui/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import {
  useGetCustomersQuery,
  useGetDashboardQuery,
  useGetEmployeesQuery,
  useGetOrdersQuery,
  useGetPaymentsQuery,
  useGetStockItemsQuery,
  useGetTasksQuery,
  useGetCashflowsQuery,
} from "../services/api";
import { money, shortDate } from "../utils/formatters";

const ReportsPage = () => {
  // Date filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const { data: dashboardData, isLoading: dashboardLoading } = useGetDashboardQuery();
  const { data: ordersData, isLoading: ordersLoading } = useGetOrdersQuery();
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentsQuery();
  const { data: customersData, isLoading: customersLoading } = useGetCustomersQuery();
  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery();
  const { data: stockData, isLoading: stockLoading } = useGetStockItemsQuery();
  const { data: tasksData, isLoading: tasksLoading } = useGetTasksQuery();
  const { data: cashflowsData, isLoading: cashflowsLoading } = useGetCashflowsQuery();

  const loading = [
    dashboardLoading,
    ordersLoading,
    paymentsLoading,
    customersLoading,
    employeesLoading,
    stockLoading,
    tasksLoading,
    cashflowsLoading,
  ].some(Boolean);

  const dashboard = dashboardData?.data;
  const orders = useMemo(() => ordersData?.data || [], [ordersData]);
  const payments = useMemo(() => paymentsData?.data || [], [paymentsData]);
  const customers = useMemo(() => customersData?.data || [], [customersData]);
  const employees = useMemo(() => employeesData?.data || [], [employeesData]);
  const stockItems = useMemo(() => stockData?.data || [], [stockData]);
  const tasks = useMemo(() => tasksData?.data || [], [tasksData]);
  const cashflows = useMemo(() => cashflowsData?.data || [], [cashflowsData]);

  // Filter data by date range
  const filterByDate = (items, dateField) => {
    if (!startDate && !endDate) return items;
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField] || item.created_at);
      const start = startDate ? new Date(startDate) : new Date("2000-01-01");
      const end = endDate ? new Date(endDate) : new Date("2099-12-31");
      
      return itemDate >= start && itemDate <= end;
    });
  };

  const filteredOrders = useMemo(() => filterByDate(orders, "created_at"), [orders, startDate, endDate]);
  const filteredPayments = useMemo(() => filterByDate(payments, "payment_date"), [payments, startDate, endDate]);
  const filteredCashflows = useMemo(() => filterByDate(cashflows, "flow_date"), [cashflows, startDate, endDate]);

  const report = useMemo(() => {
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + Number(order.total_price || 0), 0);
    const totalCollected = filteredPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const totalExpenses = filteredCashflows.reduce((sum, cf) => sum + Number(cf.amount || 0), 0);
    const netProfit = totalCollected - totalExpenses;
    const unpaidBalances = filteredOrders.reduce((sum, order) => sum + Number(order.remaining_balance || 0), 0);
    const averageOrderValue = filteredOrders.length ? totalRevenue / filteredOrders.length : 0;
    const collectionRate = totalRevenue ? (totalCollected / totalRevenue) * 100 : 0;
    const openOrders = filteredOrders.filter((order) => !["delivered", "complete"].includes(String(order.order_status))).length;
    const overdueOrders = filteredOrders.filter((order) => order.is_overdue);
    const paidOrders = filteredOrders.filter((order) => order.payment_status === "paid").length;
    const urgentOrders = filteredOrders.filter((order) => ["urgent", "high"].includes(String(order.priority))).length;

    const orderStatusCounts = filteredOrders.reduce((accumulator, order) => {
      const key = order.order_status || "unknown";
      accumulator[key] = (accumulator[key] || 0) + 1;
      return accumulator;
    }, {});

    const productMix = filteredOrders.reduce((accumulator, order) => {
      const key = order.product_type || "Other";
      accumulator[key] = {
        count: (accumulator[key]?.count || 0) + 1,
        revenue: (accumulator[key]?.revenue || 0) + Number(order.total_price || 0),
      };
      return accumulator;
    }, {});

    // Monthly Cash Flow Analysis
    const monthlyCashFlow = {};
    filteredCashflows.forEach(cf => {
      const date = new Date(cf.flow_date || cf.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyCashFlow[monthKey]) {
        monthlyCashFlow[monthKey] = { expenses: 0, month: monthKey };
      }
      monthlyCashFlow[monthKey].expenses += Number(cf.amount || 0);
    });

    filteredPayments.forEach(payment => {
      const date = new Date(payment.payment_date || payment.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyCashFlow[monthKey]) {
        monthlyCashFlow[monthKey] = { income: 0, expenses: 0, month: monthKey };
      }
      monthlyCashFlow[monthKey].income = (monthlyCashFlow[monthKey].income || 0) + Number(payment.amount || 0);
    });

    // Calculate profit for each month
    const monthlyData = Object.values(monthlyCashFlow).map(month => ({
      month: month.month,
      income: month.income || 0,
      expenses: month.expenses || 0,
      profit: (month.income || 0) - (month.expenses || 0)
    })).sort((a, b) => a.month.localeCompare(b.month));

    const rankedProductMix = Object.entries(productMix)
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.revenue - a.revenue);

    const topCustomers = [...customers]
      .sort((a, b) => Number(b.total_spend || 0) - Number(a.total_spend || 0))
      .slice(0, 5);

    const topEmployees = [...employees]
      .sort((a, b) => Number(b.completed_tasks || b.productivity_score || 0) - Number(a.completed_tasks || a.productivity_score || 0))
      .slice(0, 5);

    const lowStockItems = stockItems.filter((item) => item.low_stock);
    const atRiskStockValue = lowStockItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_cost || 0),
      0
    );

    const taskCounts = {
      todo: tasks.filter((task) => task.status === "todo").length,
      in_progress: tasks.filter((task) => task.status === "in_progress").length,
      done: tasks.filter((task) => task.status === "done").length,
    };
    const completionRate = tasks.length ? (taskCounts.done / tasks.length) * 100 : 0;

    return {
      totalRevenue,
      totalCollected,
      totalExpenses,
      netProfit,
      unpaidBalances,
      averageOrderValue,
      collectionRate,
      openOrders,
      overdueOrders,
      paidOrders,
      urgentOrders,
      orderStatusCounts,
      rankedProductMix,
      topCustomers,
      topEmployees,
      lowStockItems,
      atRiskStockValue,
      taskCounts,
      completionRate,
      monthlyData,
    };
  }, [customers, employees, filteredOrders, filteredPayments, filteredCashflows, stockItems, tasks]);

  // Export to CSV
  const exportToCSV = () => {
    const dateRange = startDate && endDate ? `${startDate}_to_${endDate}` : "all_time";
    const filename = `melona_report_${dateRange}.csv`;

    // Prepare CSV data
    let csv = "Melona Printing - Financial Report\n";
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `Date Range: ${startDate || "All"} to ${endDate || "All"}\n\n`;

    // Summary
    csv += "FINANCIAL SUMMARY\n";
    csv += "Metric,Amount (ETB)\n";
    csv += `Total Revenue,${report.totalRevenue.toFixed(2)}\n`;
    csv += `Total Collected,${report.totalCollected.toFixed(2)}\n`;
    csv += `Total Expenses,${report.totalExpenses.toFixed(2)}\n`;
    csv += `Net Profit,${report.netProfit.toFixed(2)}\n`;
    csv += `Outstanding Balance,${report.unpaidBalances.toFixed(2)}\n`;
    csv += `Average Order Value,${report.averageOrderValue.toFixed(2)}\n`;
    csv += `Collection Rate,${report.collectionRate.toFixed(2)}%\n\n`;

    // Monthly Cash Flow
    csv += "MONTHLY CASH FLOW\n";
    csv += "Month,Income (ETB),Expenses (ETB),Profit (ETB)\n";
    report.monthlyData.forEach(month => {
      csv += `${month.month},${month.income.toFixed(2)},${month.expenses.toFixed(2)},${month.profit.toFixed(2)}\n`;
    });
    csv += "\n";

    // Product Mix
    csv += "PRODUCT MIX BY REVENUE\n";
    csv += "Product Type,Orders Count,Revenue (ETB)\n";
    report.rankedProductMix.forEach(product => {
      csv += `${product.name},${product.count},${product.revenue.toFixed(2)}\n`;
    });
    csv += "\n";

    // Top Customers
    csv += "TOP CUSTOMERS\n";
    csv += "Customer Name,Company,Total Spend (ETB)\n";
    report.topCustomers.forEach(customer => {
      csv += `${customer.name},${customer.company_name || "N/A"},${Number(customer.total_spend || 0).toFixed(2)}\n`;
    });

    // Create and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !dashboard) {
    return <div className="loading-state">Building reports...</div>;
  }

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Business Intelligence"
        title="Powerful reporting across revenue, collections, order flow, workload, and stock risk."
        description="A single reporting view for leadership, operations, and finance decisions."
        action={
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FiFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              className="btn-primary"
              type="button"
              onClick={exportToCSV}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FiDownload /> Export CSV
            </button>
          </div>
        }
      />

      {/* Date Filter Section */}
      {showFilters && (
        <div className="panel" style={{ marginBottom: "1.5rem", padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FiCalendar />
              <label style={{ fontWeight: 600, fontSize: "0.875rem" }}>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "2px solid var(--border, #e5e7eb)",
                  borderRadius: "6px",
                  fontSize: "0.875rem"
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FiCalendar />
              <label style={{ fontWeight: 600, fontSize: "0.875rem" }}>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "2px solid var(--border, #e5e7eb)",
                  borderRadius: "6px",
                  fontSize: "0.875rem"
                }}
              />
            </div>
            {(startDate || endDate) && (
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
                style={{ fontSize: "0.875rem" }}
              >
                Clear Filters
              </button>
            )}
            {(startDate || endDate) && (
              <span style={{ 
                color: "var(--brand, #e8702a)", 
                fontSize: "0.875rem", 
                fontWeight: 600 
              }}>
                Showing: {startDate || "Beginning"} → {endDate || "Now"}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <StatCard label="Revenue" value={money(report.totalRevenue)} tone="success" helper={`${filteredOrders.length} orders`} />
        <StatCard label="Collected" value={money(report.totalCollected)} tone="default" helper={`${report.collectionRate.toFixed(1)}% collection rate`} />
        <StatCard label="Expenses" value={money(report.totalExpenses)} tone="warning" helper={`${filteredCashflows.length} transactions`} />
        <StatCard 
          label="Net Profit" 
          value={money(report.netProfit)} 
          tone={report.netProfit >= 0 ? "success" : "warning"} 
          helper={report.netProfit >= 0 ? "Profitable period" : "Loss period"} 
        />
      </div>

      {/* Monthly Cash Flow & Profit Section */}
      {report.monthlyData.length > 0 && (
        <div className="panel" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>Monthly Cash Flow & Profit</h3>
          <div className="table-wrap">
            <table className="data-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th style={{ textAlign: "right" }}>Income (ETB)</th>
                  <th style={{ textAlign: "right" }}>Expenses (ETB)</th>
                  <th style={{ textAlign: "right" }}>Profit/Loss (ETB)</th>
                  <th style={{ textAlign: "right" }}>Margin %</th>
                </tr>
              </thead>
              <tbody>
                {report.monthlyData.map((month) => {
                  const margin = month.income > 0 ? ((month.profit / month.income) * 100).toFixed(1) : 0;
                  return (
                    <tr key={month.month}>
                      <td style={{ fontWeight: 600 }}>{month.month}</td>
                      <td style={{ textAlign: "right", color: "var(--success, #10b981)" }}>
                        {money(month.income)}
                      </td>
                      <td style={{ textAlign: "right", color: "var(--danger, #b93826)" }}>
                        {money(month.expenses)}
                      </td>
                      <td style={{ 
                        textAlign: "right", 
                        fontWeight: 600,
                        color: month.profit >= 0 ? "var(--success, #10b981)" : "var(--danger, #b93826)"
                      }}>
                        {money(month.profit)}
                      </td>
                      <td style={{ 
                        textAlign: "right",
                        color: margin >= 0 ? "var(--success, #10b981)" : "var(--danger, #b93826)"
                      }}>
                        {margin}%
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ 
                  borderTop: "2px solid var(--border, #e5e7eb)", 
                  fontWeight: 700,
                  background: "var(--bg-subtle, #f9fafb)" 
                }}>
                  <td>TOTAL</td>
                  <td style={{ textAlign: "right", color: "var(--success, #10b981)" }}>
                    {money(report.totalCollected)}
                  </td>
                  <td style={{ textAlign: "right", color: "var(--danger, #b93826)" }}>
                    {money(report.totalExpenses)}
                  </td>
                  <td style={{ 
                    textAlign: "right",
                    color: report.netProfit >= 0 ? "var(--success, #10b981)" : "var(--danger, #b93826)"
                  }}>
                    {money(report.netProfit)}
                  </td>
                  <td style={{ 
                    textAlign: "right",
                    color: report.netProfit >= 0 ? "var(--success, #10b981)" : "var(--danger, #b93826)"
                  }}>
                    {report.totalCollected > 0 ? ((report.netProfit / report.totalCollected) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <section className="panel">
          <h3>Executive summary</h3>
          <div className="metric-row"><span>Total customers</span><strong>{customers.length}</strong></div>
          <div className="metric-row"><span>Total employees</span><strong>{employees.length}</strong></div>
          <div className="metric-row"><span>Urgent or high priority orders</span><strong>{report.urgentOrders}</strong></div>
          <div className="metric-row"><span>Overdue orders</span><strong>{report.overdueOrders.length}</strong></div>
          <div className="metric-row"><span>Low stock alerts</span><strong>{report.lowStockItems.length}</strong></div>
        </section>

        <section className="panel">
          <h3>Task performance</h3>
          <div className="metric-row"><span>To do</span><strong>{report.taskCounts.todo}</strong></div>
          <div className="metric-row"><span>In progress</span><strong>{report.taskCounts.in_progress}</strong></div>
          <div className="metric-row"><span>Done</span><strong>{report.taskCounts.done}</strong></div>
          <div className="metric-row"><span>Completion rate</span><strong>{report.completionRate.toFixed(1)}%</strong></div>
        </section>

        <section className="panel">
          <h3>Cash health</h3>
          <div className="metric-row"><span>Total payments received</span><strong>{payments.length}</strong></div>
          <div className="metric-row"><span>Collected revenue</span><strong>{money(report.totalCollected)}</strong></div>
          <div className="metric-row"><span>Outstanding balance</span><strong>{money(report.unpaidBalances)}</strong></div>
          <div className="metric-row"><span>At-risk stock value</span><strong>{money(report.atRiskStockValue)}</strong></div>
        </section>
      </div>

      <div className="dashboard-grid dashboard-grid--wide">
        <section className="panel">
          <h3>Order pipeline</h3>
          <div className="report-chip-grid">
            {Object.entries(report.orderStatusCounts).map(([status, count]) => (
              <div key={status} className="report-chip-card">
                <StatusBadge value={status} />
                <strong>{count}</strong>
              </div>
            ))}
          </div>
          <div className="stack-list">
            {report.overdueOrders.slice(0, 4).map((order) => (
              <div key={order.id} className="list-row list-row--stack">
                <div>
                  <strong>{order.title}</strong>
                  <p>{order.product_type}</p>
                </div>
                <div className="mini-metrics">
                  <span>{shortDate(order.deadline)}</span>
                  <span>{money(order.remaining_balance)}</span>
                </div>
              </div>
            ))}
            {!report.overdueOrders.length ? <div className="empty-state">No overdue orders right now.</div> : null}
          </div>
        </section>

        <section className="panel">
          <h3>Product mix by revenue</h3>
          <div className="stack-list">
            {report.rankedProductMix.slice(0, 6).map((item) => (
              <div key={item.name} className="metric-row">
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.count} orders</p>
                </div>
                <strong>{money(item.revenue)}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <h3>Top customers</h3>
          {report.topCustomers.map((customer) => (
            <div key={customer.id} className="metric-row">
              <div>
                <strong>{customer.name}</strong>
                <p>{customer.company_name || customer.customer_type}</p>
              </div>
              <strong>{money(customer.total_spend)}</strong>
            </div>
          ))}
        </section>

        <section className="panel">
          <h3>Team output</h3>
          {report.topEmployees.map((employee) => (
            <div key={employee.id} className="metric-row">
              <div>
                <strong>{employee.first_name} {employee.last_name}</strong>
                <p>{employee.role}</p>
              </div>
              <strong>{employee.completed_tasks || employee.productivity_score || 0}</strong>
            </div>
          ))}
        </section>

        <section className="panel">
          <h3>Stock risk watchlist</h3>
          {report.lowStockItems.slice(0, 5).map((item) => (
            <div key={item.id} className="metric-row">
              <div>
                <strong>{item.name}</strong>
                <p>{item.category}</p>
              </div>
              <strong>{item.quantity} {item.unit}</strong>
            </div>
          ))}
          {!report.lowStockItems.length ? <div className="empty-state">Stock levels look healthy.</div> : null}
        </section>
      </div>
    </div>
  );
};

export default ReportsPage;
